import type {
  ApiErrorBody,
  BoardSummary,
  BoardsResponse,
  ClueProblem,
  ErrorCode,
  GameResponse,
  GameView,
  GenerationResponse,
  GenerationView,
  Lang,
  TurnResponse,
} from '@shared/types';

/** Ошибка API. `network` — до сервера вообще не достучались. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network',
    message: string,
    readonly status: number,
    readonly problem?: ClueProblem,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Api {
  listBoards(lang: Lang): Promise<BoardSummary[]>;
  createGame(boardId: string): Promise<GameView>;
  getGame(id: string): Promise<GameView>;
  playTurn(id: string, clue: string): Promise<TurnResponse>;
  startGeneration(lang: Lang): Promise<GenerationView>;
  getGeneration(id: string): Promise<GenerationView>;
}

/**
 * HTTP-клиент к Worker. `baseUrl` пустой в вебе (тот же origin) и задаётся при сборке мобильной
 * оболочки (VITE_API_BASE), где приложение живёт на своём origin, а API — на workers.dev.
 */
export class HttpApi implements Api {
  constructor(
    private readonly baseUrl = '',
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  private async request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}/api${path}`, {
        method,
        headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError('network', 'Нет связи с сервером', 0);
    }
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const err = (data as ApiErrorBody | null)?.error;
      throw new ApiError(
        err?.code ?? 'internal',
        err?.message ?? `HTTP ${res.status}`,
        res.status,
        err?.problem,
      );
    }
    return data as T;
  }

  async listBoards(lang: Lang): Promise<BoardSummary[]> {
    return (await this.request<BoardsResponse>('GET', `/boards?lang=${lang}`)).boards;
  }
  async createGame(boardId: string): Promise<GameView> {
    return (await this.request<GameResponse>('POST', '/games', { boardId })).game;
  }
  async getGame(id: string): Promise<GameView> {
    return (await this.request<GameResponse>('GET', `/games/${encodeURIComponent(id)}`)).game;
  }
  playTurn(id: string, clue: string): Promise<TurnResponse> {
    return this.request<TurnResponse>('POST', `/games/${encodeURIComponent(id)}/turns`, { clue });
  }
  async startGeneration(lang: Lang): Promise<GenerationView> {
    return (await this.request<GenerationResponse>('POST', '/generations', { lang })).generation;
  }
  async getGeneration(id: string): Promise<GenerationView> {
    return (await this.request<GenerationResponse>('GET', `/generations/${encodeURIComponent(id)}`))
      .generation;
  }
}
